/* Spin-the-wheel core — pure logic, no DOM.
   Spec: docs/superpowers/specs/2026-09-15-spin-wheel-popup-design.md

   UMD: window.LTSpinWheelCore in the browser, module.exports under Node so
   scripts/spin-wheel/spin-wheel-core.test.js can pin every decision here.

   Wheel geometry: wedge i spans [i*seg, (i+1)*seg) degrees clockwise from
   12 o'clock, where the pointer sits. Rotating the wheel clockwise by R
   brings the wedge at angle (360 - R mod 360) under the pointer. */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.LTSpinWheelCore = api;
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var TIME_ZONE = 'Africa/Cairo';
  var ADULT_AGE = 16;
  var TURNS = 5;
  var EDGE_MARGIN = 0.1; /* keep the stop ≥10% of a wedge away from its edges */
  var MAX_AGE_YEARS = 100;
  var ID_PATTERN = /^[a-z0-9-]+$/;
  var ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function toInt(value) {
    var s = String(value === undefined || value === null ? '' : value).trim();
    return /^\d+$/.test(s) ? parseInt(s, 10) : NaN;
  }

  function isLeap(y) {
    return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  }

  function isRealDate(y, m, d) {
    if (isNaN(y) || isNaN(m) || isNaN(d)) return false;
    var dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
  }

  function cairoToday(now) {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(now);
    var map = {};
    for (var i = 0; i < parts.length; i++) map[parts[i].type] = parts[i].value;
    return map.year + '-' + map.month + '-' + map.day;
  }

  function parseIsoDate(str) {
    if (typeof str !== 'string') return null;
    var match = ISO_PATTERN.exec(str.trim());
    if (!match) return null;
    var y = parseInt(match[1], 10);
    var m = parseInt(match[2], 10);
    var d = parseInt(match[3], 10);
    return isRealDate(y, m, d) ? match[0] : null;
  }

  function isWindowActive(start, end, today) {
    var s = parseIsoDate(start);
    var e = parseIsoDate(end);
    var t = parseIsoDate(today);
    if (!s || !e || !t || s > e) return false;
    return s <= t && t <= e;
  }

  /* A blank/missing odds value (owner cleared the field) is NOT the same as
     an explicit 0: blank drops the wedge entirely, only an explicit number
     >= 0 is kept (so an explicit 0 still means display-only). */
  function isDrawableOdds(value) {
    if (value === null || value === undefined || value === '') return false;
    var odds = Number(value);
    return isFinite(odds) && odds >= 0;
  }

  /* Keeps every wedge the wheel can draw and land on, whether or not it
     carries a discount code. A blank code means "no-prize sign-up slice";
     an explicit odds of 0 means "shown on the wheel but never lands" (see
     pickWedge); a blank odds value drops the wedge instead (isDrawableOdds).
     isHighlighted is purely cosmetic (gold jackpot slice) — it never affects
     drawability, picking or landability. */
  function drawableWedges(wedges) {
    var counts = {};
    var i;
    for (i = 0; i < wedges.length; i++) {
      var id = String(wedges[i].id || '');
      counts[id] = (counts[id] || 0) + 1;
    }
    var out = [];
    for (i = 0; i < wedges.length; i++) {
      var w = wedges[i];
      var wid = String(w.id || '');
      var label = String(w.label || '').trim();
      if (!ID_PATTERN.test(wid) || counts[wid] > 1) continue;
      if (!label || !isDrawableOdds(w.odds)) continue;
      out.push({
        id: wid,
        label: label,
        odds: Number(w.odds),
        hasCode: w.hasCode === true,
        isHighlighted: w.highlight === true
      });
    }
    return out;
  }

  /* Weighted pick over drawableWedges' output. A 0-odds wedge has zero-width
     share of the cumulative range, so it can never satisfy `r < cumulative`
     — it is only ever a display-only slice. lastPositive guards the
     fallback so a run of trailing 0-odds wedges can't be returned either. */
  function pickWedge(wedges, rand) {
    var total = 0;
    var i;
    for (i = 0; i < wedges.length; i++) total += wedges[i].odds;
    if (!wedges.length || total <= 0) return -1;
    var r = rand() * total;
    var cumulative = 0;
    var lastPositive = -1;
    for (i = 0; i < wedges.length; i++) {
      if (wedges[i].odds > 0) lastPositive = i;
      cumulative += wedges[i].odds;
      if (r < cumulative) return i;
    }
    return lastPositive;
  }

  /* The wheel fails closed (normal popup shows) unless there are at least
     2 drawable slices and at least one is actually winnable: odds > 0 and
     carries a code. Otherwise every spin would be a guaranteed no-prize. */
  function isPlayable(wedges) {
    if (wedges.length < 2) return false;
    for (var i = 0; i < wedges.length; i++) {
      if (wedges[i].odds > 0 && wedges[i].hasCode === true) return true;
    }
    return false;
  }

  /* A saved/pending wedge id is only worth restoring if it can still be
     landed on. A 0-odds wedge (e.g. a shown-but-unwinnable prize) must never
     be resumed as a claim or success, even from stale/tampered storage —
     the owner's guarantee that it "never lands" has to hold for every path,
     not just a live pickWedge draw. */
  function indexOfLandableWedge(wedges, id) {
    if (typeof id !== 'string') return -1;
    for (var i = 0; i < wedges.length; i++) {
      if (wedges[i].id === id && wedges[i].odds > 0) return i;
    }
    return -1;
  }

  function stopAngle(index, count, rand) {
    var seg = 360 / count;
    var pointerAngle = (index + EDGE_MARGIN + rand() * (1 - 2 * EDGE_MARGIN)) * seg;
    return TURNS * 360 + (360 - pointerAngle);
  }

  function wedgeAtRotation(rotation, count) {
    var seg = 360 / count;
    var pointerAngle = (360 - (rotation % 360)) % 360;
    return Math.floor(pointerAngle / seg) % count;
  }

  function ageOn(birth, today) {
    var t = ISO_PATTERN.exec(today);
    var ty = parseInt(t[1], 10);
    var tm = parseInt(t[2], 10);
    var td = parseInt(t[3], 10);
    var bm = birth.m;
    var bd = birth.d;
    if (bm === 2 && bd === 29 && !isLeap(ty)) {
      bm = 3;
      bd = 1;
    }
    var age = ty - birth.y;
    if (tm < bm || (tm === bm && td < bd)) age -= 1;
    return age;
  }

  /* input: { d, m, y, today } — same shape as buildTags. */
  function isValidBirthdate(input) {
    var dd = toInt(input.d);
    var mm = toInt(input.m);
    var yy = toInt(input.y);
    if (!isRealDate(yy, mm, dd)) return false;
    var iso = yy + '-' + pad2(mm) + '-' + pad2(dd);
    if (iso > input.today) return false;
    return parseInt(input.today.slice(0, 4), 10) - yy <= MAX_AGE_YEARS;
  }

  function buildTags(input) {
    var d = toInt(input.d);
    var m = toInt(input.m);
    var y = toInt(input.y);
    var tags = [
      'newsletter',
      'popup',
      'spin-wheel',
      'prize-' + input.wedgeId,
      'bday-' + pad2(d) + '-' + pad2(m),
      'bday-month-' + pad2(m)
    ];
    if (ageOn({ y: y, m: m, d: d }, input.today) >= ADULT_AGE) {
      tags.push('born-' + y);
    }
    return tags.join(',');
  }

  return {
    ADULT_AGE: ADULT_AGE,
    cairoToday: cairoToday,
    parseIsoDate: parseIsoDate,
    isWindowActive: isWindowActive,
    drawableWedges: drawableWedges,
    pickWedge: pickWedge,
    isPlayable: isPlayable,
    indexOfLandableWedge: indexOfLandableWedge,
    stopAngle: stopAngle,
    wedgeAtRotation: wedgeAtRotation,
    ageOn: ageOn,
    isValidBirthdate: isValidBirthdate,
    buildTags: buildTags
  };
});
